import { CategoryId } from '$/epg/categories'
import { ContentTypeDefault } from '@setplex/tria-api'
import removeAccents from 'remove-accents'
import { env } from '~/app/environment'
import {
  HOME_REGIONS,
  HOME_REGIONS_ALIAS_IDS,
  HOME_REGIONS_ALIAS_REGION_NAMES,
  HOME_REGIONS_IDS_ALIAS,
  QueryType,
  ROUTE_NAME,
} from '../constants'
import { PARAM, RESERVED_NAME, type ReservedNameType } from '../constants/path'
import { type PlayerSearchParams, type UrlSearchParams } from '../types'
import { slugify } from './strings'

/**
 * Removes special characters from a string
 * Characters to remove: _ . ~ ! * ' ( ) ; : @ & = + $ , | / \ ? % # [ ]
 * @param str - Input string
 * @returns String without special characters
 */
const removeSpecialChars = (str: string): string => {
  return str.replace(/[_.~!*'();:@&=+$,|/?%#[\]\\]/g, '')
}

/**
 * Formats a string for use in URLs by:
 * 1. Removing special characters - _ . ~ ! * ' ( ) ; : @ & = + $ , | / \ ? % # [ ]
 * 2. Converting to lowercase
 * 3. Removing accents from characters
 * 4. Replacing spaces with hyphens
 * 5. Adding a trailing hyphen
 *
 * @example
 * formatTitleForUrl('Hello World!') // returns 'hello-world-'
 * formatTitleForUrl('Test@#$%') // returns 'test-'
 * formatTitleForUrl('Café & Restaurant') // returns 'cafe--restaurant-'
 * formatTitleForUrl('My (awesome) title') // returns 'my-awesome-title-'
 *
 * @param {string | undefined} title - The string to format
 * @returns {string} Formatted string safe for use in URLs, with trailing hyphen
 */
export const formatTitleForUrl = (title: string | undefined): string => {
  if (!title) return ''

  return `${slugify(removeSpecialChars(removeAccents(title)))}-`
}

/**
 * Function get substring after last hyphens in string.
 * Used to get 'id' 93 from URL {path}/sports-news-93
 * @param string
 */
// Does not work with 0 value (f.e. category 'All') - returns null
export const getIdFromUrl = (string = ''): number | null => {
  const id = Number(string.split('-').pop())
  return id || null
}

/**
 * Get id and/or title from url like 'award-winner-3', 'award%20winner-3', 'award%20winner', 'award-winner', '3', 'all-0'
 * @param string
 */
export const getDataFromUrl = (
  string = ''
): { id: number | null; title: string } => {
  if (!string) return { id: null, title: '' }

  const parts = string.split('-')
  const lastPart = parts[parts.length - 1]
  const potentialId = Number(lastPart)

  // Check if last part is a valid number (including 0)
  if (!isNaN(potentialId) && lastPart !== '') {
    return {
      id: potentialId,
      title: parts.slice(0, -1).join('-'),
    }
  }

  return {
    id: null,
    title: string,
  }
}

/**
 * Extended version of getDataFromUrl that also handles special reserved values.
 * Get id and/or title from url like:
 * - 'award-winner-3' -> {id: 3, title: 'award-winner'}
 * - 'award%20winner-3' -> {id: 3, title: 'award%20winner'}
 * - 'award%20winner' -> {id: null, title: 'award%20winner'}
 * - '3' -> {id: 3, title: ''}
 * - 'all' -> {id: 'all', title: 'all'}
 * - 'my-list' -> {id: 'my-list', title: 'my-list'}
 * - 'null' -> {id: null, title: ''}
 * @param string
 */
export const getDataFromUrlExtended = (
  string = ''
): { id: number | ReservedNameType | null; title: string } => {
  // Handle special reserved values
  if (string === RESERVED_NAME.ALL || string === RESERVED_NAME.MY_LIST) {
    return {
      id: string,
      title: string,
    }
  }

  if (!string || string === 'null') return { id: null, title: '' }

  const parts = string.split('-')
  const lastPart = parts[parts.length - 1]
  const potentialId = Number(lastPart)

  // Check if last part is a valid number (including 0)
  if (!isNaN(potentialId) && lastPart !== '') {
    return {
      id: potentialId,
      title: parts.slice(0, -1).join('-'),
    }
  }

  return {
    id: null,
    title: string,
  }
}

/**
 * Extracts a ID from a URL search parameter
 *
 * @param {string} param - The name of the search parameter to extract
 * @param {URLSearchParams | undefined} query - The URLSearchParams object to search in
 * @returns {number | undefined} The numeric ID if found and valid, undefined otherwise
 *
 * @example
 * getIdFromQuery('channel', new URLSearchParams('?channel=123'))
 * // Returns 123
 *
 * getIdFromQuery('channel', new URLSearchParams('?channel=abc'))
 * // Returns undefined
 *
 * getIdFromQuery('channel', new URLSearchParams())
 * // Returns undefined
 *
 * getIdFromQuery('channel', new URLSearchParams('?channel=null'))
 * // Returns undefined
 *
 * getIdFromQuery('channel', new URLSearchParams('?channel=undefined'))
 * // Returns undefined
 */

export function getIdFromQuery(
  param: string,
  query?: URLSearchParams | undefined
): number | undefined {
  const id = query?.get(param)
  if (id == null) return undefined
  const idn = Number(id)
  if (isNaN(idn)) return undefined
  return idn
}

export const getQueryByContentType = (type: ContentTypeDefault): string => {
  switch (type) {
    case ContentTypeDefault.movie:
      return QueryType.movies
    case ContentTypeDefault.tvshow:
      return QueryType.tvshows
    case ContentTypeDefault.liveChannel:
      return QueryType.live
    default:
      return ''
  }
}

/**
 * Adds search parameters to a URL path (it does not add search params to existing search params)
 *
 * @param {Object} params - The parameters to add to the URL
 * @param {string} path - The base URL path to add parameters to (should not include search params)
 * @returns {string} The URL with added search parameters
 * 
 * @example
 * addSearchParamsToPath({
 *   path: "/live/channel-123/play",
 *   params: { category: 456 }
 * })
 * // Returns "/live/channel-123/play?category=456"

 */
export const addSearchParamsToPath = ({
  path,
  params,
}: {
  path: string
  params: PlayerSearchParams
}): string => {
  const searchParams: Record<string, string> = {}

  for (const [key, value] of Object.entries(params)) {
    if (value != null) {
      searchParams[key] = value.toString()
    }
  }

  return `${path}${Object.keys(searchParams).length > 0 ? `?${new URLSearchParams(searchParams)}` : ''}`
}

/**
 * Extracts search parameters from a URL search string
 *
 * @param {string} search - The URL search string (e.g. "?category=123")
 * @returns {Object} An object containing the extracted parameters
 */
export const getSearchParams = (
  search: string
): {
  categoryId?: PlayerSearchParams['category']
  channelId?: PlayerSearchParams['channel']
  seasonId?: PlayerSearchParams['season']
  episodeId?: PlayerSearchParams['episode']
  regionId?: PlayerSearchParams['region']
  genreId?: UrlSearchParams['genre']
  languageId?: UrlSearchParams['language']
} => {
  const sParams = new URLSearchParams(search)

  // Live channel/EPG
  const categoryId = sParams.get(PARAM.CATEGORY)
  const channelId = sParams.get('channel')
  const regionId = sParams.get(PARAM.REGION)

  // TV Show
  const seasonId = sParams.get('season')
  const episodeId = sParams.get('episode')

  const genreId = sParams.get('genre')
  const languageId = sParams.get('language')

  return {
    categoryId,
    channelId,
    seasonId,
    episodeId,
    regionId,
    genreId,
    languageId,
  }
}

export function getCategoryIdFromSegment(
  segment?: string
): number | CategoryId | undefined {
  if (!segment) return undefined

  const { id } = getDataFromUrlExtended(segment)
  if (id == null) return undefined

  // Handle reserved values
  if (id === RESERVED_NAME.ALL) {
    return CategoryId.All
  }
  if (id === RESERVED_NAME.MY_LIST) {
    return CategoryId.Favorite
  }

  // Handle numeric values
  const idn = Number(id)
  if (isNaN(idn)) return undefined

  return idn
}

export const getQueryParamsFromUrl = (
  href?: string
): { utm_custom_param?: string | null } => {
  const { searchParams: s, pathname } = new URL(href ?? window.location.href)
  const allLandingPages = Object.keys(HOME_REGIONS)

  // If landing page, return empty utm_custom_param
  if (allLandingPages.some((page) => pathname.startsWith(`/${page}`))) {
    return {
      utm_custom_param: '',
    }
  }
  return {
    utm_custom_param: s.has(env.UVO_UTM_PARAMETER)
      ? s.get(env.UVO_UTM_PARAMETER)
      : '',
  }
}

export const getRegionFromUrl = (
  href?: string
): { name: string; alias: string; id: number } | null => {
  const { pathname } = new URL(href ?? window.location.href)

  // Match landing pages: /arabworld, /ukraine, /latinamerica, etc.
  const allLandingPages = Object.keys(
    HOME_REGIONS
  ) as (keyof typeof HOME_REGIONS)[]
  const matchedPage = allLandingPages.find((page) =>
    pathname.startsWith(`/${page}`)
  )

  if (matchedPage) {
    const alias = HOME_REGIONS[
      matchedPage
    ] as keyof typeof HOME_REGIONS_ALIAS_REGION_NAMES
    return {
      name: HOME_REGIONS_ALIAS_REGION_NAMES[alias] ?? '',
      alias,
      id: HOME_REGIONS_ALIAS_IDS[alias],
    }
  }

  // Match category pages: /live/categories/arab-world-292, /live/categories/ukraine-230, etc.
  if (pathname.startsWith(ROUTE_NAME.LIVE_CATEGORIES)) {
    const slug = pathname.split('/').pop() ?? ''
    const categoryId = Number(slug.split('-').pop())
    const alias = categoryId ? HOME_REGIONS_IDS_ALIAS[categoryId] : undefined

    if (alias) {
      return {
        name: HOME_REGIONS_ALIAS_REGION_NAMES[alias] ?? '',
        alias,
        id: categoryId,
      }
    }
  }

  return null
}
